/**
 * Resolve Names, the portal's step, in the e-file app's review before the upload.
 *
 * Mirrors tab-portal src/pages/files/select-file/resolve-names (qa c50f5268): the same summary
 * tiles, badges, filters, table columns, wording and consent. The names are read here in the
 * browser with the portal's rules, the uncertain tail goes to POST /v1/names/identify (names
 * only), and nothing is uploaded until the person accepts. Their decisions ride the upload
 * link, so the server files each name as it was shown here.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  deriveSplitNameFields,
  inferNameOrder,
  mapDerivedToSplitColumns,
  normalizeNameKey,
} from '@engine/intake/nameIdentification';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Pill } from '@/components/ui/pill';
import { SortableTH, Table, TableWrap, TBody, TD, TH, THead, TR, useTableSort } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/toggles';
import { errorMessage, tabfile } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  AI_AUTO_PASS_LIMIT,
  AI_CHUNK_SIZE,
  buildFullDecisionSet,
  buildNameResolutionPlan,
  buildPub1220NamePlan,
  checkPub1220NameOrder,
  classify1098TStudentNames,
  classifyLocally,
  composeSplitDisplayName,
  confidenceBand,
  DEFAULT_REVIEW_THRESHOLD,
  exportNameDecisionsCsv,
  identityColumns,
  is1098TFormType,
  mergeAiResults,
  nameDecisionsForUpload,
  previewPub1220Name,
  RESOLVE_NAMES_PAGE_SIZE,
  REVIEW_THRESHOLD_OPTIONS,
  summarizeDecisions,
  tinKindOf,
  toggleDecision as toggled,
  type NameDecision,
} from './name-resolution';
import type { PrecheckResult } from './precheck';

type FilterMode = 'review' | 'all' | 'ai';

/**
 * The names of a checked file and how they were decided. Lives with the review so the footer
 * can hold the person on this part until they accept, and the upload can carry the answers.
 */
export function useNameResolution(review: PrecheckResult | null) {
  const formType = review?.detected.formType ?? '';
  const records = useMemo(() => review?.records ?? [], [review]);
  const columns = useMemo(() => identityColumns(review?.mapping), [review]);
  // An IRS 1220 file has no name columns: its payee name lines are the names to resolve.
  const pub1220 = useMemo(() => (review?.payeeNames ? buildPub1220NamePlan(review.payeeNames) : null), [review]);
  const plan = useMemo(
    () => pub1220 ?? buildNameResolutionPlan(formType, columns, records),
    [pub1220, formType, columns, records],
  );
  const is1098T = is1098TFormType(formType);

  const [decisions, setDecisions] = useState<NameDecision[]>([]);
  const [consented, setConsented] = useState(false);
  const [isClassifying, setIsClassifying] = useState(false);
  const [isAiRunning, setIsAiRunning] = useState(false);
  const [usedLiveModel, setUsedLiveModel] = useState(false);
  /** Names in flight to the AI pass: drives the progress banner. */
  const [aiPendingCount, setAiPendingCount] = useState(0);
  const [aiUnavailable, setAiUnavailable] = useState(false);
  /**
   * Set when the request itself failed (signed out, a server error, a timeout on the way), as
   * opposed to the service answering that the AI was unavailable. Both used to read as the AI
   * being down, which hid the cause.
   */
  const [aiRequestError, setAiRequestError] = useState<string | null>(null);
  const [remainingForAi, setRemainingForAi] = useState<string[]>([]);
  /** Guards the one-shot classification per checked file. */
  const classifiedFor = useRef<PrecheckResult | null>(null);

  const runAiPass = useCallback(async (names: string[], base: NameDecision[]) => {
    if (names.length === 0) return base;
    let merged = base;
    for (let offset = 0; offset < names.length; offset += AI_CHUNK_SIZE) {
      const chunk = names.slice(offset, offset + AI_CHUNK_SIZE);
      setAiPendingCount(names.length - offset);
      const response = await tabfile.POST('/v1/names/identify', { body: { names: chunk } });
      if (response.unavailable) {
        setAiUnavailable(true);
        setUsedLiveModel(false);
        return merged;
      }
      setAiUnavailable(false);
      setAiRequestError(null);
      setUsedLiveModel(response.usedLiveModel);
      merged = mergeAiResults(
        merged,
        response.results.map((result) => ({ ...result, source: 'ai' as const })),
      );
    }
    return merged;
  }, []);

  // Classify once per checked file. A new file, or the same file checked again, starts over.
  useEffect(() => {
    if (classifiedFor.current === review) return;
    classifiedFor.current = review;
    setDecisions([]);
    setConsented(false);
    setUsedLiveModel(false);
    setAiUnavailable(false);
    setAiRequestError(null);
    setRemainingForAi([]);
    setAiPendingCount(0);
    if (!plan.required) return;

    let cancelled = false;
    const withPreSplit = (legacy: NameDecision[]) => {
      const all = buildFullDecisionSet(plan, legacy, records, columns, formType);
      return pub1220 ? checkPub1220NameOrder(all, pub1220.payeeByName) : all;
    };

    if (is1098T) {
      setDecisions(withPreSplit(classify1098TStudentNames(plan.distinctNames)));
      return;
    }

    const { decisions: local, pending } = classifyLocally(
      plan.distinctNames,
      plan.tinHintByName,
      plan.tinHintSourceByName,
    );
    setDecisions(withPreSplit(local));
    if (pending.length === 0) return;

    setIsClassifying(true);
    const autoNames = pending.slice(0, AI_AUTO_PASS_LIMIT);
    setRemainingForAi(pending.slice(AI_AUTO_PASS_LIMIT));
    setAiPendingCount(autoNames.length);
    runAiPass(autoNames, local)
      .then((mergedLegacy) => {
        if (!cancelled) setDecisions(withPreSplit(mergedLegacy));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setAiUnavailable(true);
        setAiRequestError(errorMessage(error));
      })
      .finally(() => {
        if (cancelled) return;
        setIsClassifying(false);
        setAiPendingCount(0);
      });
    return () => {
      cancelled = true;
      // A cancelled pass must not leave the step stuck on "Identifying…".
      setIsClassifying(false);
      setAiPendingCount(0);
    };
  }, [review, plan, pub1220, records, columns, formType, is1098T, runAiPass]);

  const resolveRemainingWithAi = useCallback(async () => {
    setIsAiRunning(true);
    try {
      const legacyOnly = decisions.filter((decision) => !decision.customerSplitProvided);
      const mergedLegacy = await runAiPass(remainingForAi, legacyOnly);
      const all = buildFullDecisionSet(plan, mergedLegacy, records, columns, formType);
      setDecisions(pub1220 ? checkPub1220NameOrder(all, pub1220.payeeByName) : all);
      setRemainingForAi([]);
    } catch (error) {
      setAiUnavailable(true);
      setAiRequestError(errorMessage(error));
    } finally {
      setIsAiRunning(false);
    }
  }, [decisions, remainingForAi, runAiPass, plan, pub1220, records, columns, formType]);

  const toggle = useCallback((name: string) => {
    setDecisions((previous) => toggled(previous, name));
  }, []);

  // Pub 1220 writes the surname first, and the name control checks it line by line.
  const nameOrder = useMemo(
    () => (pub1220 ? ('surname-first' as const) : inferNameOrder(plan.distinctNames.slice(0, 500))),
    [pub1220, plan.distinctNames],
  );

  /** True once the step is done: not needed, or accepted with nothing still in flight. */
  const ready = !plan.required || (consented && !isClassifying && decisions.length > 0);

  return {
    formType,
    records,
    columns,
    plan,
    pub1220,
    is1098T,
    decisions,
    consented,
    setConsented,
    isClassifying,
    isAiRunning,
    usedLiveModel,
    aiPendingCount,
    aiUnavailable,
    aiRequestError,
    remainingForAi,
    resolveRemainingWithAi,
    toggle,
    nameOrder,
    ready,
    /** What rides the upload link; undefined when the file has nothing to resolve. */
    forUpload: () => {
      if (!plan.required) return undefined;
      const answers = nameDecisionsForUpload(decisions, DEFAULT_REVIEW_THRESHOLD);
      return answers.length > 0 ? answers : undefined;
    },
  };
}

export type NameResolutionState = ReturnType<typeof useNameResolution>;

/** One tile of the summary row. */
function Stat({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number | string;
  tone?: 'default' | 'good' | 'warn' | 'bad';
}) {
  return (
    <div className="rounded-lg border border-[color:var(--ts-border-soft)] bg-surface px-4 py-3">
      <div
        className={cn(
          'text-2xl font-semibold tabular-nums',
          tone === 'good'
            ? 'text-[color:var(--ts-pill-success-text)]'
            : tone === 'warn'
              ? 'text-[color:var(--ts-pill-warning-text)]'
              : tone === 'bad'
                ? 'text-destructive'
                : 'text-gray-3',
        )}
      >
        {value}
      </div>
      <div className="mt-0.5 text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

/** The step itself: summary, filters, the table, and the consent the upload waits on. */
export function ResolveNames({ names }: { names: NameResolutionState }) {
  const {
    formType,
    records,
    columns,
    plan,
    pub1220,
    is1098T,
    decisions,
    consented,
    setConsented,
    isClassifying,
    isAiRunning,
    usedLiveModel,
    aiPendingCount,
    aiUnavailable,
    aiRequestError,
    remainingForAi,
    resolveRemainingWithAi,
    toggle,
    nameOrder,
  } = names;

  const [filter, setFilter] = useState<FilterMode>('review');
  const [threshold, setThreshold] = useState<number>(DEFAULT_REVIEW_THRESHOLD);
  const [page, setPage] = useState(0);

  const summary = useMemo(() => summarizeDecisions(decisions, threshold), [decisions, threshold]);
  const belowThreshold = useMemo(
    () => decisions.filter((decision) => decision.conflict || decision.confidence < threshold),
    [decisions, threshold],
  );
  const aiAssisted = useMemo(() => decisions.filter((decision) => decision.source === 'ai'), [decisions]);
  const visible = filter === 'review' ? belowThreshold : filter === 'ai' ? aiAssisted : decisions;

  const { sort, onSort, sorted } = useTableSort(
    visible,
    {
      name: (decision) => decision.name,
      classification: (decision) => decision.classification,
      confidence: (decision) => decision.confidence,
    },
    { key: 'confidence', direction: 'asc' },
  );

  // A different row set under the person starts at the first page, as on the portal.
  useEffect(() => setPage(0), [visible.length, filter, threshold]);

  const rowByName = useMemo(() => {
    const map = new Map<string, { name1: string; name2: string; index: number }>();
    for (const row of plan.rows) {
      const key = normalizeNameKey(row.displayName || row.name1 || row.name2);
      if (key && !map.has(key)) map.set(key, { name1: row.name1, name2: row.name2, index: row.index });
    }
    return map;
  }, [plan.rows]);

  const previewFor = useCallback(
    (decision: NameDecision) => {
      const row = rowByName.get(normalizeNameKey(decision.name));
      if (pub1220) {
        const control = pub1220.payeeByName[normalizeNameKey(decision.name)]?.nameControl ?? '';
        return previewPub1220Name(decision, row, control, is1098T);
      }
      if (decision.customerSplitProvided) {
        const data = row ? records[row.index] : undefined;
        if (data) {
          const filed = composeSplitDisplayName(data, columns, formType);
          if (filed) return filed;
        }
      }
      const derived = deriveSplitNameFields(row?.name1 ?? decision.name, row?.name2 ?? '', nameOrder, {
        classificationOverride: is1098T ? 'individual' : decision.classification,
      });
      if (is1098T) {
        const { columns: split, unplacedLine2 } = mapDerivedToSplitColumns(formType, derived);
        const filed = [split.STUDENT_FIRST_NAME, split.STUDENT_MIDDLE_NAME, split.STUDENT_LAST_NAME, split.STUDENT_SUFFIX]
          .filter(Boolean)
          .join(' · ');
        return unplacedLine2 ? `${filed} (line 2 not filed: ${unplacedLine2})` : filed;
      }
      return decision.classification === 'business'
        ? [derived.RECIPIENT_BUSINESS_NAME1, derived.RECIPIENT_BUSINESS_NAME2].filter(Boolean).join(' / ')
        : [
            derived.RECIPIENT_FIRST_NAME,
            derived.RECIPIENT_MIDDLE_NAME,
            derived.RECIPIENT_LAST_NAME,
            derived.RECIPIENT_SUFFIX,
          ]
            .filter(Boolean)
            .join(' · ');
    },
    [rowByName, pub1220, records, columns, formType, nameOrder, is1098T],
  );

  if (!plan.required) return null;

  const total = sorted.length;
  const pageCount = Math.max(1, Math.ceil(total / RESOLVE_NAMES_PAGE_SIZE));
  const pageIndex = Math.min(page, pageCount - 1);
  const pageRows = sorted.slice(pageIndex * RESOLVE_NAMES_PAGE_SIZE, (pageIndex + 1) * RESOLVE_NAMES_PAGE_SIZE);
  const rangeStart = total === 0 ? 0 : pageIndex * RESOLVE_NAMES_PAGE_SIZE + 1;
  const rangeEnd = Math.min((pageIndex + 1) * RESOLVE_NAMES_PAGE_SIZE, total);
  const autoResolved = summary.total - summary.belowThreshold;
  const autoPercent = summary.total > 0 ? Math.round((autoResolved / summary.total) * 100) : 0;
  const fmt = (n: number) => n.toLocaleString();
  const columnCount = is1098T ? 5 : 6;

  return (
    <div className="flex flex-col" data-testid="resolve-names">
      <div className="mb-4">
        <h3 className="text-xl font-semibold text-gray-3">Resolve Names</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {pub1220
            ? 'Your IRS 1220 file has one name line per payee. We identified each name as a person or a business, using the Type of TIN where the file gives one, so it can be filed in the 2026 split name fields. Review anything flagged below.'
            : `Your file uses combined name columns (${plan.legacyColumns?.join(', ')}). We identified each name as a person or a business so it can be filed in the 2026 split name fields. Review anything flagged below.`}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat
          label={
            plan.rows.length && plan.rows.length !== summary.total
              ? `Distinct names (${fmt(plan.rows.length)} rows)`
              : 'Distinct names'
          }
          value={fmt(summary.total)}
        />
        <Stat label="Individuals" value={fmt(summary.individual)} />
        <Stat label="Businesses" value={fmt(summary.business)} />
        <Stat
          label={`Confident (${Math.round(threshold * 100)}%+)`}
          value={isClassifying ? '—' : `${autoPercent}%`}
          tone="good"
        />
        <Stat
          label="Needs review"
          value={fmt(summary.belowThreshold)}
          tone={summary.belowThreshold > 0 ? 'warn' : 'good'}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Pill tone={usedLiveModel ? 'info' : 'neutral'}>{usedLiveModel ? 'AI-assisted' : 'Matched by rules'}</Pill>
        {summary.aiAssisted > 0 ? <span>{summary.aiAssisted} name(s) needed AI assistance</span> : null}
        {summary.conflicts > 0 ? (
          <Pill tone="danger">{summary.conflicts} name(s) disagree with the tax ID</Pill>
        ) : null}
        {summary.overridden > 0 ? <span>{summary.overridden} confirmed by you</span> : null}
        <span>
          {pub1220
            ? 'Name order: Last First, as Pub 1220 writes it, checked against the name control'
            : `Name order detected: ${nameOrder === 'surname-first' ? 'Last First' : 'First Last'}`}
        </span>
      </div>

      {aiRequestError ? (
        <p className="mt-2 text-xs text-muted-foreground">
          We could not reach the AI name check ({aiRequestError}), so a few names were identified using our standard
          rules only. You can still review and continue, or reload the page to try again.
        </p>
      ) : aiUnavailable ? (
        <p className="mt-2 text-xs text-muted-foreground">
          AI assistance is temporarily unavailable, so a few names were identified using our standard rules only. You
          can still review and continue.
        </p>
      ) : null}

      <p className="mt-3 text-xs text-muted-foreground">
        {pub1220
          ? 'Taxpayer ID numbers are never sent to the AI service — only the name text is. The Type of TIN in your file is used locally to decide the result, and a name with one is not sent for AI review, even if the name itself looks like the other type.'
          : 'Taxpayer ID numbers are never sent to the AI service — only the name text is. A formatted SSN or EIN in your file is used locally to decide the result and is not sent for AI review, even if the name itself looks like the other type.'}
      </p>

      {aiPendingCount > 0 ? (
        <div className="mt-4 flex items-center gap-3 rounded-lg border border-[color:var(--ts-pill-info-border)] bg-[color:var(--ts-pill-info-bg)] px-4 py-3">
          <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent motion-reduce:animate-none" />
          <span className="text-xs text-gray-3">
            Asking AI to help with {aiPendingCount.toLocaleString()} name(s) our rules could not identify confidently.
            This usually takes a few seconds.
          </span>
        </div>
      ) : null}

      {remainingForAi.length > 0 ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[color:var(--ts-pill-warning-border)] bg-[color:var(--ts-pill-warning-bg)] px-4 py-3">
          <span className="text-xs text-gray-3">
            {remainingForAi.length.toLocaleString()} more uncertain name(s) were not sent for AI assistance automatically,
            to keep this file fast.
          </span>
          <Button variant="outline" size="sm" disabled={isAiRunning} onClick={() => void resolveRemainingWithAi()}>
            {isAiRunning ? 'Resolving…' : `Resolve ${remainingForAi.length.toLocaleString()} with AI`}
          </Button>
        </div>
      ) : null}

      <div className="mb-3 mt-6 flex flex-wrap items-center gap-2">
        <Button size="sm" variant={filter === 'review' ? 'default' : 'outline'} onClick={() => setFilter('review')}>
          Needs review ({belowThreshold.length.toLocaleString()})
        </Button>
        <Button size="sm" variant={filter === 'all' ? 'default' : 'outline'} onClick={() => setFilter('all')}>
          All names ({decisions.length.toLocaleString()})
        </Button>
        <Button size="sm" variant={filter === 'ai' ? 'default' : 'outline'} onClick={() => setFilter('ai')}>
          AI-assisted ({aiAssisted.length.toLocaleString()})
        </Button>
        {filter === 'review' ? (
          <div className="ml-2 flex items-center gap-2 text-xs text-muted-foreground">
            <span>Show confidence below</span>
            <div className="w-24">
              <Combobox
                ariaLabel="Show confidence below"
                value={String(threshold)}
                onValueChange={(value) => setThreshold(Number(value))}
                options={REVIEW_THRESHOLD_OPTIONS.map((option) => ({
                  value: String(option),
                  label: `${Math.round(option * 100)}%`,
                }))}
              />
            </div>
          </div>
        ) : null}
        <Button
          size="sm"
          variant="outline"
          className="ml-auto"
          disabled={decisions.length === 0 || isClassifying}
          onClick={() => exportNameDecisionsCsv(decisions, previewFor)}
        >
          Export as CSV
        </Button>
      </div>

      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        {total > 0 ? (
          <span className="max-w-3xl text-xs text-muted-foreground">
            Everything here is already identified — only use <span className="font-medium">No, it&apos;s a …</span> where
            we got it wrong. Accept them all at the bottom of the page.
          </span>
        ) : (
          <span />
        )}
        {total > 0 ? (
          <span className="ml-auto whitespace-nowrap text-xs text-muted-foreground">
            Showing {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of {total.toLocaleString()}
          </span>
        ) : null}
      </div>

      <TableWrap>
        <Table>
          <THead>
            <tr>
              <SortableTH columnKey="name" sort={sort} onSort={onSort}>
                Name in file
              </SortableTH>
              <SortableTH columnKey="classification" sort={sort} onSort={onSort}>
                Identified as
              </SortableTH>
              <SortableTH columnKey="confidence" sort={sort} onSort={onSort}>
                Confidence
              </SortableTH>
              <TH>Why</TH>
              <TH>Will be filed as</TH>
              {is1098T ? null : <TH align="right">Change</TH>}
            </tr>
          </THead>
          <TBody>
            {isClassifying && total === 0 ? (
              <TR>
                <TD colSpan={columnCount} className="py-10 text-center text-sm">
                  Identifying names…
                </TD>
              </TR>
            ) : null}
            {pageRows.map((decision) => {
              const band = confidenceBand(decision);
              return (
                <TR key={decision.name}>
                  <TD className="min-w-[180px] py-3">
                    <span className="font-medium">{decision.name}</span>
                  </TD>
                  <TD className="whitespace-nowrap py-3">
                    <Pill tone={decision.classification === 'business' ? 'info' : 'success'}>
                      {decision.classification === 'business' ? 'Business' : 'Individual'}
                    </Pill>
                  </TD>
                  <TD className="min-w-[160px] py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={cn(
                          'tabular-nums',
                          band === 'high'
                            ? 'text-[color:var(--ts-pill-success-text)]'
                            : band === 'medium'
                              ? 'text-[color:var(--ts-pill-warning-text)]'
                              : 'text-destructive',
                        )}
                      >
                        {Math.round(decision.confidence * 100)}%
                      </span>
                      {decision.customerSplitProvided ? <Pill tone="success">Identified</Pill> : null}
                      {tinKindOf(decision) && !decision.customerSplitProvided ? (
                        <Pill tone="success">{tinKindOf(decision) === 'business' ? 'EIN' : 'SSN'}</Pill>
                      ) : null}
                      {decision.conflict ? <Pill tone="danger">TIN conflict</Pill> : null}
                      {decision.source === 'ai' ? <Pill tone="info">AI</Pill> : null}
                      {decision.overridden ? <Pill tone="neutral">You</Pill> : null}
                    </div>
                  </TD>
                  <TD className="min-w-[260px] py-3">
                    <span className="text-xs text-muted-foreground">{decision.signal}</span>
                  </TD>
                  <TD className="min-w-[170px] py-3">
                    <span className="text-xs">{previewFor(decision)}</span>
                  </TD>
                  {is1098T ? null : (
                    <TD className="py-3">
                      {decision.customerSplitProvided ? (
                        <span className="block text-right text-[11px] text-muted-foreground">Already split in file</span>
                      ) : (
                        <div className="flex justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-auto whitespace-nowrap px-2 py-1 text-[11px] font-normal"
                            onClick={() => toggle(decision.name)}
                          >
                            No, it&apos;s a {decision.classification === 'business' ? 'person' : 'business'}
                          </Button>
                        </div>
                      )}
                    </TD>
                  )}
                </TR>
              );
            })}
            {!isClassifying && total === 0 ? (
              <TR>
                <TD colSpan={columnCount} className="py-10 text-center text-sm">
                  {filter === 'review'
                    ? `Nothing below ${Math.round(threshold * 100)}% and no TIN/name conflicts — every name was identified confidently.`
                    : filter === 'ai'
                      ? 'No names needed AI assistance — every name was resolved by our rules or a formatted SSN/EIN.'
                      : 'No names found in the mapped columns.'}
                </TD>
              </TR>
            ) : null}
          </TBody>
        </Table>
      </TableWrap>

      {pageCount > 1 ? (
        <div className="mt-3 flex items-center justify-between">
          <Button variant="outline" size="sm" disabled={pageIndex === 0} onClick={() => setPage(pageIndex - 1)}>
            Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {(pageIndex + 1).toLocaleString()} of {pageCount.toLocaleString()}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={pageIndex >= pageCount - 1}
            onClick={() => setPage(pageIndex + 1)}
          >
            Next
          </Button>
        </div>
      ) : null}

      <label
        htmlFor="accept-name-identifications"
        className="mt-6 flex cursor-pointer items-start gap-3 rounded-lg border border-[color:var(--ts-border-soft)] bg-[color:var(--ts-row-hover)] p-4"
      >
        <span className="mt-0.5">
          <Checkbox
            id="accept-name-identifications"
            checked={consented}
            label="I accept these name identifications and confirm they are correct."
            onCheckedChange={setConsented}
          />
        </span>
        <span className="text-xs text-muted-foreground">
          <span className="font-medium text-gray-3">
            I accept these name identifications and confirm they are correct.
          </span>{' '}
          Each name shows how it was identified and how confident we are. You can change any of them above before
          accepting.
        </span>
      </label>
    </div>
  );
}
